import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { SlideMenu } from 'apps/sigma2-ui/src/components/SlideMenu';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { SimpleSelect } from 'apps/sigma2-ui/src/components/ui/SelectComponent/SimpleSelect';
import { getDatabaseRevisionsForUser } from 'libs/end-to-end/src/get-database-revisions-for-user';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useDashboards } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';

const LATEST_OPTION_VALUE = 'latest';

export const DashboardSelectDataSourceSlideMenu = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: Setter<boolean>;
}) => {
  const language = useLanguage();
  const { myUser } = useUser();
  const { updateDashboardDraftDatabaseRevisionId, dashboardDraft } =
    useDashboards();
  const dbRevisionDropdownOptions = useMemo(
    () => [
      {
        label: 'Latest',
        value: LATEST_OPTION_VALUE,
      },
      ...getDatabaseRevisionsForUser({
        myUser,
        databaseId: dashboardDraft?.record?.databaseId ?? '',
      }),
    ],
    [myUser, dashboardDraft?.record?.databaseId],
  );

  const effectiveDatabaseRevisionId =
    dashboardDraft?.draft.databaseRevisionId ??
    dashboardDraft?.record?.databaseRevisionId ??
    null;

  const [selectedDatabaseRevision, setSelectedDatabaseRevision] = useState<
    string | null
  >(effectiveDatabaseRevisionId);

  useEffect(() => {
    setSelectedDatabaseRevision(effectiveDatabaseRevisionId);
  }, [effectiveDatabaseRevisionId]);

  const handleSelect = useCallback((newSelection: string) => {
    if (newSelection === LATEST_OPTION_VALUE) {
      setSelectedDatabaseRevision(null);
    } else {
      setSelectedDatabaseRevision(newSelection);
    }
  }, []);

  const handleSave = useCallback(() => {
    updateDashboardDraftDatabaseRevisionId(selectedDatabaseRevision);
    setIsOpen(false);
  }, [
    selectedDatabaseRevision,
    setIsOpen,
    updateDashboardDraftDatabaseRevisionId,
  ]);

  return (
    <SlideMenu
      show={isOpen}
      onClose={() => setIsOpen(false)}
      width="w-1/3"
      qaDataName={'dashboard-select-data-source-slide-menu'}
    >
      <div className="-m-6">
        <Spacing
          direction="horizontal"
          size="lg"
          className="px-8 py-4 bg-gray-50 border-gray-200 border-b-1 border-solid items-center justify-between"
        >
          <S2Typography
            translatedContent={language.dashboardSelectDataSourceSlideHeading()}
            variant={'headline-5'}
            textColorVariant={'gray-900'}
          />
          <PrimaryButtonLightBg
            onClick={handleSave}
            disabled={false}
            translatedContent={language.save()}
            innerClassName="py-1 !px-4"
          />
        </Spacing>
        <div className="px-8 py-6 border-gray-200 border-b-1 border-solid">
          <S2Typography
            translatedContent={language.dashboardSelectDataSourceSlideDescription()}
            variant={'body-6'}
            textColorVariant={'gray-900'}
          />
        </div>
        <div className="px-8 pt-6">
          <S2Typography
            translatedContent={language.dashboardSelectDataSourceSlideSelectorLabel()}
            variant={'body-6-medium'}
            textColorVariant={'gray-500'}
          />
          <div className="w-fit pt-4 pb-2 min-w-[170px]">
            <SimpleSelect
              selectedOptionValue={
                selectedDatabaseRevision ?? LATEST_OPTION_VALUE
              }
              updateOptions={handleSelect}
              options={dbRevisionDropdownOptions}
            />
          </div>
          <S2Typography
            translatedContent={language.dashboardSelectDataSourceSlideSelectorInfo(
              {
                refreshDt: new Date(),
                isLatestDataSource: selectedDatabaseRevision === null,
              },
            )}
            variant={'body-6'}
            textColorVariant={'gray-600'}
          />
        </div>
      </div>
    </SlideMenu>
  );
};
